import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Types (mirror backend/src/modules/settings)
// ---------------------------------------------------------------------------

export interface CompanyDetails {
  legalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  taxId: string | null;
  /**
   * INVOICE LOGO OVERRIDE — null for almost everyone. The logo comes from the
   * hub (see `OrgSettings.logo`); this is only for a different mark on
   * client-facing invoices, and null falls back to the hub logo.
   */
  logoUrl: string | null;
}

/** Where the invoice logo comes from, and whether the PDF can carry it. */
export interface OrgLogo {
  source: 'hub' | 'override' | 'none';
  /** The logo every surface renders. */
  url: string | null;
  /** The Solid Digital accounts organization's logo, in use or not. */
  hubUrl: string | null;
  overrideUrl: string | null;
  /**
   * Whether the PDF writer can embed it. `false` = the printed sheet shows the
   * logo and the PDF shows the company name — the screen says so.
   */
  printable: boolean | null;
}

export interface OrgSettings {
  id: string;
  name: string;
  slug: string;
  currency: string;
  fiscalYearStartMonth: number;
  paymentTermsDays: number;
  invoiceFooterNote: string | null;
  /** Remittance block printed on every invoice, one instruction per line. */
  paymentInstructions: string | null;
  /** Policy: every agreement must be linked to a hub project. */
  requireHubLink: boolean;
  company: CompanyDetails;
  logo: OrgLogo;
}

export interface OrgSettingsPatch {
  name?: string;
  currency?: string;
  fiscalYearStartMonth?: number;
  paymentTermsDays?: number;
  invoiceFooterNote?: string | null;
  paymentInstructions?: string | null;
  company?: Partial<CompanyDetails>;  requireHubLink?: boolean;
}

export interface HarvestConnection {
  provider: 'HARVEST';
  status: string; // connected | error | disconnected
  accountId: string;
  connectedAs: string | null;
  companyName: string | null;
  lastSyncAt: string | null;
}

export interface QboConnection {
  provider: 'QBO';
  status: string; // connected | not_connected
  connected: boolean;
  lastSyncAt: string | null;
}

export interface Integrations {
  harvest: HarvestConnection | null;
  qbo: QboConnection;
}

export interface ImportCounts {
  clients: number;
  people: number;
  tasks: number;
  agreements: number;
  agreementTasks: number;
  timeEntries: number;
  invoices: number;
  lineItems: number;
  payments: number;
  skipped: number;
}

export type ImportPhase =
  | 'starting'
  | 'clients'
  | 'people'
  | 'tasks'
  | 'agreements'
  | 'agreement_tasks'
  | 'time_entries'
  | 'invoices'
  | 'payments'
  | 'linking'
  | 'done';

export interface ImportRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'success' | 'error' | string;
  stats: { phase?: ImportPhase; counts?: Partial<ImportCounts> };
  error: string | null;
}

/**
 * Scheduled jobs. Nothing in this app runs on an in-process timer (they die on
 * scale-to-zero), so an external scheduler POSTs `/api/cron/*` — and "nobody
 * installed it" is a silent failure this panel exists to make loud.
 */
export type JobHealth = 'ok' | 'late' | 'stale' | 'never';

export interface ScheduledJob {
  job: string;
  label: string;
  path: string;
  cron: string;
  cadence: string;
  why: string;
  ifItStops: string;
  everyMinutes: number;
  staleAfterMinutes: number;
  lastSuccessAt: string | null;
  ageMinutes: number | null;
  status: JobHealth;
  summary: string | null;
  durationMs: number | null;
}

export interface ScheduledJobs {
  now: string;
  /** The worst job's health — never an average. */
  health: JobHealth;
  neverRun: number;
  stale: number;
  jobs: ScheduledJob[];
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

const KEY = ['settings'] as const;

export function useScheduledJobs() {
  return useQuery({
    queryKey: [...KEY, 'scheduled-jobs'],
    queryFn: () => api.get<ScheduledJobs>('/settings/scheduled-jobs'),
    // Ages are computed server-side; a minute's refresh keeps "12 min ago"
    // honest while the tab is open without polling anything expensive.
    refetchInterval: 60_000,
  });
}

export function useOrgSettings() {
  return useQuery({
    queryKey: [...KEY, 'org'],
    queryFn: () => api.get<OrgSettings>('/settings/org'),
  });
}

export function useIntegrations() {
  return useQuery({
    queryKey: [...KEY, 'integrations'],
    queryFn: () => api.get<Integrations>('/settings/integrations'),
  });
}

/** Import history — polls every 1.5s while a run is in flight. */
export function useImportRuns() {
  return useQuery({
    queryKey: [...KEY, 'import', 'runs'],
    queryFn: () => api.get<{ runs: ImportRun[] }>('/settings/import/runs'),
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) => run.status === 'running') ? 1500 : false,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useUpdateOrgSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: OrgSettingsPatch) => api.patch<OrgSettings>('/settings/org', patch),
    onSuccess: (data) => {
      queryClient.setQueryData([...KEY, 'org'], data);
    },
  });
}

export function useConnectHarvest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { token: string; accountId: string }) =>
      api.post<HarvestConnection>('/settings/import/harvest', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...KEY, 'integrations'] }),
  });
}

export function useRunImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ImportRun>('/settings/import/harvest/run'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...KEY, 'import', 'runs'] });
      void queryClient.invalidateQueries({ queryKey: [...KEY, 'integrations'] });
    },
  });
}

export function useRemoveHarvest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<{ removed: boolean }>('/settings/import/harvest'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...KEY, 'integrations'] }),
  });
}

// ---------------------------------------------------------------------------
// Stripe / card payments — READ, never re-derived
// ---------------------------------------------------------------------------

/**
 * What this deployment can do with a card, straight from the server's own mode
 * ladder (`backend/src/modules/invoices/invoice-payment.ts#resolveInvoicePaymentMode`,
 * surfaced by `InvoicePaymentService.deploymentStatus()`).
 *
 * ⚠ **Never re-implement the ladder in the browser.** `test` is structurally
 * impossible in production and `INVOICE_CARD_PAYMENTS=off` can only make things
 * quieter — rules that live in one pure function on the server and must keep
 * living there. Settings only PRINTS the verdict.
 */
export interface PaymentDeployment {
  mode: 'live' | 'test' | 'off';
  /**
   * WHERE THE CREDENTIAL CAME FROM. Since key management moved into the app
   * (Jesse, 2026-08-13) a key can live in the database as well as in the
   * environment, and a mode with no provenance is how the wrong environment
   * ends up talking to production.
   */
  source: 'database' | 'environment' | 'none';
  live: boolean;
  test: boolean;
  reason: string | null;
  /** Stripe payment-method types the checkout would offer. */
  methods: string[];
  /** Bank debit is available (a $5 cap instead of ~2.9% of a large invoice). */
  ach: boolean;
  /** Where the switch lives — this screen, or the env vars. Never a secret. */
  configuredBy: string;
}

export interface DeliveryDeployment {
  mode: 'live' | 'file' | 'off' | string;
  transmits: boolean;
  writesToDisk: boolean;
  from: string;
  fileDir: string;
  reason: string | null;
  hostedLinks: string;
  payments: PaymentDeployment;
}

/** Powers the Stripe row on Settings → Integrations, and its own screen. */
export function useDeliveryDeployment() {
  return useQuery({
    queryKey: [...KEY, 'delivery'],
    queryFn: () => api.get<DeliveryDeployment>('/invoices/delivery'),
  });
}

// ---------------------------------------------------------------------------
// Stripe — THE CREDENTIAL, managed in the app
// ---------------------------------------------------------------------------

/**
 * Jesse, 2026-08-13: *"key management should be done in the app"*.
 *
 * `GET /settings/integrations/stripe` is the whole picture — which source is in
 * use, what the key is (a prefix and a masked tail, never a secret), what
 * Stripe said it opens, who connected it and when, and what the one mode ladder
 * makes of it. **Every write returns the same refreshed state**, so a mutation
 * seeds the cache and no follow-up GET is fired.
 *
 * ⚠ Nothing on this payload is spendable — the server asserts that over every
 * response it can produce — so it may be rendered verbatim. There is no reveal
 * route and no field that could hold a key.
 */
export interface StripeKeyRead {
  /** `sk_test_51H…••••1234`. The only rendering of a key this app emits. */
  masked: string | null;
  prefix: string | null;
  last4: string | null;
  mode: 'test' | 'live' | null;
  kind: 'sk' | 'rk' | null;
}

export interface StripeAccountRead {
  id: string;
  displayName: string | null;
  email: string | null;
  country: string | null;
  chargesEnabled: boolean | null;
  livemode: boolean | null;
}

/** One row of the audit trail. Immutable, newest first, capped by the server. */
export interface CredentialEvent {
  id: string;
  action: string;
  summary: string;
  detail: string | null;
  ok: boolean;
  actorName: string | null;
  actorEmail: string | null;
  at: string;
}

export interface StripeConnection {
  provider: 'STRIPE';
  /** Which credential this deployment is actually using for this org. */
  source: 'database' | 'environment' | 'none';
  /** The server's own sentence naming it. Printed, never paraphrased. */
  sourceLabel: string;
  stored: boolean;
  usable: boolean;
  status: string | null;
  key: StripeKeyRead;
  webhook: { prefix: string | null; present: boolean };
  connectedAs: {
    accountId: string | null;
    name: string | null;
    country: string | null;
    chargesEnabled: boolean | null;
    livemode: boolean | null;
  } | null;
  connectedAt: string | null;
  connectedBy: string | null;
  rotatedAt: string | null;
  rotatedBy: string | null;
  lastTestedAt: string | null;
  lastTestOk: boolean | null;
  lastTestReason: string | null;
  problems: Array<{ key: string; message: string }>;
  reason: string | null;
  /**
   * Where card payments land with no stored credential — the server's own
   * sentence, so the confirmation states it BEFORE the button is pressed and
   * the `DELETE` answer states the same thing after.
   */
  fallsBackTo: string;
  /** The ONE mode ladder's verdict — read, never re-derived in the browser. */
  payments: {
    mode: 'live' | 'test' | 'off';
    reason: string | null;
    source: 'database' | 'environment' | 'none';
    /** Set while a stored key is held but the environment still takes payments. */
    transportNote: string | null;
  };
  /** Whether the CALLER may write. Reads are open; writes are owner/admin. */
  canManage: boolean;
  history: CredentialEvent[];
}

export interface StripeTestResult {
  ok: boolean;
  source: StripeConnection['source'];
  sourceLabel: string;
  checkedAt: string;
  latencyMs: number | null;
  account: StripeAccountRead | null;
  reason: string | null;
  detail: string | null;
}

const STRIPE_KEY = [...KEY, 'stripe'] as const;

export function useStripeConnection() {
  return useQuery({
    queryKey: STRIPE_KEY,
    queryFn: () => api.get<StripeConnection>('/settings/integrations/stripe'),
  });
}

/**
 * Connect / rotate / disconnect all move the payment mode, so they invalidate
 * `/invoices/delivery` too — the composer, the chase dialog and the invoice
 * screen print that verdict above the fold before anything is sent.
 */
function useStripeWrite<TInput>(fn: (input: TInput) => Promise<StripeConnection>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (state) => {
      queryClient.setQueryData(STRIPE_KEY, state);
      void queryClient.invalidateQueries({ queryKey: [...KEY, 'delivery'] });
    },
    // ⚠ A REFUSED ATTEMPT IS ALSO A ROW IN THE TRAIL. The server records the
    // keys it would not store — that is half the point of the trail — so a
    // failed save must refresh the status, or the history card on screen would
    // be missing the very attempt the reader just made.
    onError: () => queryClient.invalidateQueries({ queryKey: STRIPE_KEY }),
  });
}

export function useConnectStripe() {
  return useStripeWrite((payload: { secretKey: string; webhookSecret: string }) =>
    api.post<StripeConnection>('/settings/integrations/stripe', payload),
  );
}

/** Either secret alone; the other is kept. A failed rotation changes nothing. */
export function useRotateStripe() {
  return useStripeWrite((payload: { secretKey?: string; webhookSecret?: string }) =>
    api.post<StripeConnection>('/settings/integrations/stripe/rotate', payload),
  );
}

/** Tests whatever is IN USE — stored row or environment — and says which. */
export function useTestStripe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<StripeTestResult>('/settings/integrations/stripe/test'),
    // The test stamps lastTestedAt/lastTestOk on a stored row and appends to
    // the trail, so the status is refetched rather than patched here.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: STRIPE_KEY }),
  });
}

export function useDisconnectStripe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.delete<{ removed: boolean; fallsBackTo: string }>('/settings/integrations/stripe'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STRIPE_KEY });
      void queryClient.invalidateQueries({ queryKey: [...KEY, 'delivery'] });
    },
  });
}
